import { createContext, useContext, useState, useEffect } from "react";
import { getGeoInfo } from "@/lib/geo";

const RegionContext = createContext();

export const useRegion = () => useContext(RegionContext);

// AED and SAR are both pegged to USD, so static rates are stable.
export const CURRENCIES = {
  AED: { rate: 3.6725, symbol: "د.إ", en: "AED", code: "AED" },
  SAR: { rate: 3.75, symbol: "ر.س", en: "SAR", code: "SAR" },
  USD: { rate: 1, symbol: "$", en: "USD", code: "USD" },
};

// Jingdong Logistics (JDL) warehouse data
export const WAREHOUSES = {
  uae: {
    id: "uae",
    label: "Dubai · UAE",
    labelAr: "دبي · الإمارات",
    code: "C0000001232",
    company: "Jingdong Logistics (JDL)",
    address: "Saih Shuaib 3, Dubai Industrial City, Dubai, UAE",
    googleLink: "https://maps.app.goo.gl/nrFMva7ZAXkkhFkb8",
    contactPerson: "Yousaf Naeem",
    phone: "+86 18218291826",
    email: "eztirechanger@gmail.com",
    hours: "Sunday–Thursday, 08:00–13:00 & 14:00–17:00 (lunch break 13:00–14:00)",
    mapEmbed:
      "https://www.google.com/maps?q=Saih+Shuaib+3+Dubai+Industrial+City+Dubai+UAE&output=embed",
    currency: "AED",
    language: "ar",
  },
  sa: {
    id: "sa",
    label: "Riyadh · Saudi Arabia",
    labelAr: "الرياض · المملكة العربية السعودية",
    code: "RUH-SM-1-SA",
    company: "Jingdong Logistics Service Company",
    address:
      "RMFE7628, 7628 Wadi Sa'iyah, 4413, Al-Misfah District, Riyadh 14542, Saudi Arabia",
    googleLink:
      "https://www.google.com/maps/search/?api=1&query=7628+Wadi+Sa%27iyah+Al-Misfah+District+Riyadh+14542+Saudi+Arabia",
    contactPerson: "Muhammad Ismail",
    phone: "+966598971298",
    email: "muhammad.ismail1@jd.com",
    hours: "Mon–Sat, 08:00–13:00 & 14:00–17:00 (lunch break 13:00–14:00)",
    mapEmbed:
      "https://www.google.com/maps?q=7628+Wadi+Sa%27iyah+Al-Misfah+District+Riyadh+14542+Saudi+Arabia&output=embed",
    currency: "SAR",
    language: "ar",
  },
};

export function RegionProvider({ children }) {
  const [region, setRegion] = useState(
    () => localStorage.getItem("lctx_region") || null
  );
  const [loading, setLoading] = useState(!region);

  const resolveAndPersist = (r) => {
    localStorage.setItem("lctx_region", r);
    setRegion(r);
    setLoading(false);
  };

  useEffect(() => {
    if (region) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    const detect = async () => {
      try {
        // Shared backend geo lookup (one call for RegionContext + GeoGate)
        // instead of the rate-limited ipapi.co from every visitor's browser.
        const data = await getGeoInfo();
        if (cancelled) return;
        const cc = data?.country?.toUpperCase?.();
        let detected = "uae";
        if (cc === "SA") detected = "sa";
        else if (cc === "AE") detected = "uae";
        else detected = "uae"; // default to UAE (primary market)
        resolveAndPersist(detected);
      } catch (e) {
        if (cancelled) return;
        resolveAndPersist("uae");
      }
    };

    detect();
    return () => {
      cancelled = true;
    };
  }, [region]);

  const effectiveRegion = region || "uae";
  const warehouse = WAREHOUSES[effectiveRegion] || WAREHOUSES.uae;
  // Always USD. Stripe checkout was previously charging AED/SAR for UAE/Saudi
  // customers (warehouse.currency), but a live Dubai-IP test confirmed this
  // Stripe account cannot settle AED or SAR at all -- checkout failed outright.
  // Displaying AED/SAR here while Stripe actually charges USD would also just
  // be a second, separate bug (site says one number, Stripe shows another).
  // Keep showing USD everywhere until a currency-capable gateway is in place.
  const currency = CURRENCIES.USD;
  const language = warehouse.language;
  const direction = language === "ar" ? "rtl" : "ltr";

  // Convert USD amount to the local currency and format
  const formatLocalPrice = (usdAmount) => {
    const local = (Number(usdAmount) || 0) * currency.rate;
    return `${local.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${currency.symbol}`;
  };

  const setRegionManual = (r) => {
    if (WAREHOUSES[r]) resolveAndPersist(r);
  };

  return (
    <RegionContext.Provider
      value={{
        region: effectiveRegion,
        warehouse,
        currency,
        // Same USD-only reasoning as `currency` above -- warehouse.currency
        // (AED/SAR) is kept only as reference data on WAREHOUSES, not shown.
        currencyCode: currency.code,
        language,
        direction,
        loading,
        setRegion: setRegionManual,
        formatLocalPrice,
        rate: currency.rate,
      }}
    >
      {children}
    </RegionContext.Provider>
  );
}